import { ref } from 'vue'
import { defineStore } from 'pinia'
import { secureLocalStorage } from '@/utils/secureLocalStorage'

const STORAGE_PRIMARY = 'app-theme-primary'
const STORAGE_DARK = 'app-theme-dark'

export const DEFAULT_PRIMARY = '#0369a1'

/** 预设主题色 */
export const THEME_PRESETS = [
  '#0369a1',
  '#1677ff',
  '#52c41a',
  '#fa8c16',
  '#722ed1',
  '#eb2f96',
  '#13c2c2',
  '#f5222d',
  '#2f54eb',
] as const

function normalizeHex(hex: string): string {
  let h = hex.trim()
  if (!h.startsWith('#')) h = `#${h}`
  if (h.length === 4) {
    return `#${h[1]}${h[1]}${h[2]}${h[2]}${h[3]}${h[3]}`
  }
  if (!/^#[0-9A-Fa-f]{6}$/.test(h)) return DEFAULT_PRIMARY
  return h
}

function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const h = normalizeHex(hex)
  const n = parseInt(h.slice(1), 16)
  if (Number.isNaN(n)) return null
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 }
}

/** 用于菜单选中背景等 */
export function primarySoftRgba(hex: string, alpha: number): string {
  const rgb = hexToRgb(hex)
  if (!rgb) return `rgba(22, 119, 255, ${alpha})`
  return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`
}

function applyToDocument(primary: string, dark: boolean) {
  const root = document.documentElement
  root.dataset.theme = dark ? 'dark' : 'light'
  root.style.setProperty('--app-primary', primary)
  const softA = dark ? 0.2 : 0.12
  const hoverA = dark ? 0.1 : 0.06
  root.style.setProperty('--app-primary-soft', primarySoftRgba(primary, softA))
  root.style.setProperty('--app-primary-soft-hover', primarySoftRgba(primary, hoverA))
  root.style.setProperty('--nav-sidebar-active-bar', primary)
}

export const useThemeStore = defineStore('theme', () => {
  const primaryColor = ref(DEFAULT_PRIMARY)
  const isDark = ref(false)

  function syncDom() {
    applyToDocument(primaryColor.value, isDark.value)
  }

  function setPrimaryColor(hex: string) {
    primaryColor.value = normalizeHex(hex)
    secureLocalStorage.setItem(STORAGE_PRIMARY, primaryColor.value)
    syncDom()
  }

  function setDark(value: boolean) {
    isDark.value = value
    secureLocalStorage.setItem(STORAGE_DARK, value ? '1' : '0')
    syncDom()
  }

  function toggleDark() {
    setDark(!isDark.value)
  }

  function init() {
    const savedP = secureLocalStorage.getItem(STORAGE_PRIMARY)
    const savedD = secureLocalStorage.getItem(STORAGE_DARK)
    if (savedP) primaryColor.value = normalizeHex(savedP)
    if (savedD === '1') isDark.value = true
    syncDom()
  }

  return {
    primaryColor,
    isDark,
    setPrimaryColor,
    setDark,
    toggleDark,
    init,
  }
})
